import { Grid, Tooltip } from '@material-ui/core';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineClose, AiOutlinePlus } from 'react-icons/ai';
import { FaEdit, FaPencilAlt } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { Link, useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import { MultiSelect } from '../../../../../components/MultiSelectChip';
import PageTitle from '../../../../../components/PageTitle';
import Select from '../../../../../components/Select';
import Slider from '../../../../../components/Slider';
import { IUser, useAuth } from '../../../../../hooks/auth';
import { useToast } from '../../../../../hooks/toast';
import { IAlert, IDevice, ISensor } from '../../../../../interfaces';
import { IClient } from '../../../../../interfaces/IClient';
import { IIndicator } from '../../../../../interfaces/IIndicator';
import api from '../../../../../services/api';
import getValidationErrors from '../../../../../utils/getValidationsErrors';
import * as S from './styles';

type DeviceWithAlerts = {
  indicators: IIndicator[];
  alerts: IAlert[];
  batteryAlerts: {
    id: string;
    message: string;
    timestamp: Date;
  }[];
} & IDevice;

interface ICardAlert {
  id: string;
  timestamp: Date;
  message: string;
}

const versionOptions = [
  { id: '1', name: 'Default' },
  { id: '2', name: 'Multiple' },
  { id: '3', name: 'Ayyeka' },
  { id: '4', name: 'SW8 PRO' }
];

const channelTypes = [
  { id: '3', name: 'VBat' },
  { id: '4', name: 'Temp' },
  { id: '6', name: 'GSM Signal' },
  { id: '7', name: 'GPS' },
  { id: '9', name: 'Digital' },
  { id: '11', name: 'Serial SINT32' },
  { id: '13', name: 'Analog S32' },
  { id: '15', name: 'LORA Signal' },
  { id: '101', name: 'Battery Consumption' },
  { id: '102', name: 'Communication Status' }
];

const analogPortOptions = [
  { id: 'porta_analogica_1', name: 'Porta Analógica 1' },
  { id: 'porta_analogica_2', name: 'Porta Analógica 2' },
  { id: 'contador_pulsos_total', name: 'Contador de pulsos total' },
  { id: 'contador_pulsos_parcial', name: 'Contador de pulsos parcial' },
  { id: 'pulsos_por_segundo', name: 'Pulsos por segundo' },
];

const EditDevices: React.FC = () => {
  const { addToast } = useToast();
  const params = useParams() as { id: string };
  const [usersForDeviceList, setUsersForDeviceList] = useState<IUser[]>([] as IUser[]);
  const [device, setDevice] = useState<DeviceWithAlerts>({} as DeviceWithAlerts);
  const [alerts, setAlerts] = useState<ICardAlert[]>([]);
  const [sensors, setSensors] = useState<ISensor[]>([]);
  const [indicators, setIndicators] = useState<IIndicator[]>([]);
  const [deleteSensor, setDeleteSensor] = useState('');
  const [newSensor, setNewSensor] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [error, setError] = useState('');
  const sensorFormRef = useRef<FormHandles>(null);
  const { user: authUser } = useAuth();
  const history = useHistory();
  const [selectedUsers, setSelectedUsers] = useState<{ value: string, label: string }[]>([] as { value: string, label: string }[]);
  const [deleteIndicator, setDeleteIndicator] = useState('');
  const [editNameModal, setEditNameModal] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [clientList, setClientList] = useState<IClient[]>([] as IClient[]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [updatingClient, setUpdatingClient] = useState(false);
  const [editMacModal, setEditMacModal] = useState(false);
  const [macAddress, setMacAddress] = useState('');

  const isClientAdmin = authUser.sysadmin || authUser.client_admin
  const canChangeDeviceClient = authUser.sysadmin;
  const getClientList = useCallback(async () => {
    try {
      const { data } = await api.get('clients');
      setClientList(data);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao buscar clientes',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast]);



  const esperar = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };


  const searchUsersForDeviceList = useCallback(async (client_id: string | undefined = undefined) => {
    //const searchUser = deviceFormRef.current?.getFieldValue('user');

    if (client_id === undefined && !isClientAdmin) {
      client_id = authUser.client_id;
    }

    try {
      const { data } = await api.get('profiles', { params: { client_id } });

      if (authUser.permissions?.includes('|users-managers|') || authUser.sysadmin || authUser.client_admin) {
        return setUsersForDeviceList([authUser, ...data]);
      }

      return setUsersForDeviceList(data);
    } catch (error) {
      return addToast({
        type: 'error',
        title: 'Erro ao buscar usuários',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, authUser, isClientAdmin]);

  const searchDevice = useCallback(async () => {
    try {
      const { data } = await api.get<DeviceWithAlerts>(`devices/${params.id}`);

      const sensorAlerts = data.alerts.map(item => ({ id: item.id, timestamp: item.timestamp, message: item.message }));
      const batteryAlerts = data.batteryAlerts.map(item => ({ id: item.id, timestamp: item.timestamp, message: item.message }));
      const allAlerts = [...sensorAlerts, ...batteryAlerts]

      setSensors(data.sensors);
      // setIndicators(data.indicators)

      setAlerts(allAlerts);
      setDevice(data);
      setSelectedClientId(data.client?.id || '');
      setBatteryAlert(data.battery_alert);
      await searchUsersForDeviceList(data.client.id);
      setSelectedUsers(data.users.map(u => ({ value: u.id, label: u.name })));
    } catch (error) {
      const message = (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`;

      addToast({
        type: 'error',
        title: 'Erro ao buscar dispositivo',
        description: message,
      });

      setError(message);
    }
  }, [addToast, params.id, searchUsersForDeviceList]);

  const handleSelectChange = useCallback((options: { value: string; label: string }[]) => {
    setSelectedUsers(options);
  }, []);

  useEffect(() => {
    searchDevice();

    if (canChangeDeviceClient) {
      getClientList();
    }

    //tive que colocar o codigo abaixo para esconder o separador do select, pois ele estava aparecendo
    //e não consegui resolver de outra forma
    let element = document.getElementsByClassName('css-1u9des2-indicatorSeparator');
    if (element[0]) {
      element[0].setAttribute('style', 'display: none;');
    }
  }, [canChangeDeviceClient, getClientList, searchDevice]);

  const [batteryAlert, setBatteryAlert] = useState<boolean>(device.battery_alert);

  const handleDeleteSensor = useCallback(async (id: string) => {
    try {
      await api.delete('/sensors', { params: { id } });

      setDeleteSensor('');

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Sensor excluído com sucesso!',
      });

      searchDevice();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir sensor',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, searchDevice]);

  const handleDeleteIndicator = useCallback(async (id: string) => {
    try {
      await api.delete(`/indicators/${id}`);

      setDeleteIndicator('');

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Indicador excluído com sucesso!',
      });

      searchDevice();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao excluir indicador',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, searchDevice, setDeleteIndicator]);

  const handleUpdateDeviceName = useCallback(async () => {
    try {
      if (!deviceName.trim()) {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'O nome do dispositivo não pode estar vazio.',
        });
        return;
      }

      await api.patch(`/devices/${device.id}/name`, { name: deviceName.trim() });

      setEditNameModal(false);
      setDeviceName('');

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Nome do dispositivo atualizado com sucesso!',
      });

      // Refresh device data
      searchDevice();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar nome',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, device.id, deviceName, searchDevice]);

  const handleUpdateDeviceClient = useCallback(async (clientId: string) => {
    if (!canChangeDeviceClient) {
      addToast({
        type: 'error',
        title: 'Acesso negado',
        description: 'Somente usuários sysadmin podem alterar o cliente do dispositivo.',
      });
      return;
    }

    if (!clientId || clientId === selectedClientId) {
      return;
    }

    const previousClientId = selectedClientId;
    setSelectedClientId(clientId);
    setUpdatingClient(true);

    try {
      await api.patch(`/devices/${device.id}/client`, { client_id: clientId });

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Cliente do dispositivo atualizado com sucesso!',
      });

      await searchDevice();
    } catch (error) {
      setSelectedClientId(previousClientId);
      addToast({
        type: 'error',
        title: 'Erro ao atualizar cliente',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    } finally {
      setUpdatingClient(false);
    }
  }, [addToast, canChangeDeviceClient, device.id, searchDevice, selectedClientId]);

  const normalizeMacAddress = useCallback((value: string) => {
    const onlyHex = value.replace(/[^a-fA-F0-9]/g, '').toUpperCase().slice(0, 12);
    const groups = onlyHex.match(/.{1,2}/g);

    return groups ? groups.join(':') : '';
  }, []);

  const isValidMacAddress = useCallback((value: string) => {
    return /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(value);
  }, []);

  const handleUpdateMacAddress = useCallback(async () => {
    try {
      if (!isValidMacAddress(macAddress)) {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Digite um endereço MAC válido no formato AA:BB:CC:DD:EE:FF.',
        });
        return;
      }

      await api.patch(`/devices/${device.id}/mac-address`, { mac_address: macAddress });

      setEditMacModal(false);
      setMacAddress('');

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Endereço MAC atualizado com sucesso!',
      });

      searchDevice();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar endereço MAC',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, [addToast, device.id, isValidMacAddress, macAddress, searchDevice]);

  const sensorFormHandler = useCallback(
    async formData => {
      try {
        sensorFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          sensor: Yup.string().required(),
          unit: Yup.string().required(),
          port: Yup.string().required(),
          channel_type: String(device.version) === '3'
            ? Yup.string().required('Channel type é obrigatório')
            : Yup.string().notRequired()
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        const { sensor, unit, port, channel_type, is_cumulative } = formData;
        const isVersion4Device = String(device?.version ?? '') === '4';
        const numericPort = Number(port);

        const dataToSend = {
          name: sensor,
          channel_type: Number(channel_type),
          unit_of_measurement: unit,
          device_id: params.id,
          is_cumulative: is_cumulative,
          port: isVersion4Device ? 0 : Number.isNaN(numericPort) ? 0 : numericPort,
          reading: isVersion4Device ? port : null,
        };

        await api.post('/sensors', dataToSend);

        addToast({
          type: 'success',
          title: 'Sensor criado!',
          description: 'Sensor criado com sucesso!',
        });
        sensorFormRef.current?.reset();
        setNewSensor(false);
        return searchDevice();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          return sensorFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao criar novo sensor',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast, params.id, searchDevice, device],
  );

  const fakeDevice = {
    id: '1',
    name: 'Dispositivo X',
    user: { name: 'Usuário X' },
    mac_address: '00:1B:44:11:3A:B7',
    battery: 75,
    limit: 80,
  };

  const deviceFormRef = useRef<FormHandles>(null);
  const [loader, setLoader] = useState(false);

  const updateDeviceFormHandler = useCallback(
    async formData => {

      setLoader(true);

      try {

        deviceFormRef.current?.setErrors({});

        const defaultSchema = Yup.object().shape({
          battery_alert: Yup.boolean().required(),
        });

        formData.users = selectedUsers.map((u) => {
          return {
            id: u.value,
            name: u.label,
          }
        });

        const schemaWithAlertFields = Yup.object().shape({
          battery_alert: Yup.boolean().required(),
          battery_email_alert: Yup.boolean().required(),
          battery_alert_limit: Yup.number()
            .required()
            .min(0, 'O valor mínimo do limite de bateria é 0.')
            .max(100, 'O valor máximo do limite de bateria é 100.'),
          users: Yup.array().min(1, 'Selecione pelo menos um usuário'),
          version: Yup.string().required()
        });

        if (formData.battery_alert) {
          schemaWithAlertFields.validate(formData, { abortEarly: false })
            .then((validData) => {
              console.log('Parsed data:', validData);   // shows version value
            })
            .catch((err: Yup.ValidationError) => {
              console.log(err.errors);                  // see validation messages
            });

        } else {
          await defaultSchema.validate(formData, {
            abortEarly: false,
          });
        }



        const { battery_alert, battery_email_alert, battery_alert_limit, latitude, longitude, users, version } = formData;

        const dataToSend = {
          battery_alert,
          battery_email_alert: battery_alert ? battery_email_alert : undefined,
          battery_alert_limit: battery_alert ? battery_alert_limit : null,
          latitude: !latitude || latitude.trim() === "" ? null : latitude.trim(),
          longitude: !longitude || longitude.trim() === "" ? null : longitude.trim(),
          users,
          version
        };

        await api.put(`/devices/${device.id}`, dataToSend);

        await searchDevice();

        deviceFormRef.current?.reset();

        addToast({
          type: 'success',
          title: 'Dispositivo atualizado!',
          description: 'Dispositivo atualizado com sucesso!',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          addToast({
            type: 'error',
            title: 'Erro ao atualizar dispositivo',
            description: `${error.message}`,
          });

          return deviceFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao atualizar dispositivo',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      } finally {
        setLoader(false);
      }
    },
    [addToast, device, selectedUsers],
  );

  const formRef = useRef<FormHandles>(null);

  const infoDevice = useMemo(() => {
    const submitForm = () => {
      if (formRef.current) {
        formRef.current.submitForm();
      }
    };

    return (
      <>
        <div>
          <Form ref={formRef} className="device" onSubmit={updateDeviceFormHandler}>
            <div className="device">
              <span className="device-name-field">
                <h2>Nome

                </h2>
                <div className="device-name-row">
                  <p className="device-name-value">{device.name}</p>
                  <button
                    className="edit-name-button"
                    type="button"
                    onClick={() => {
                      setDeviceName(device.name);
                      setEditNameModal(true);
                    }}
                    title="Editar nome do dispositivo"
                  >
                    <FaPencilAlt size={14} />
                  </button>
                </div>
              </span>
              {canChangeDeviceClient && (
                <span>
                  <h2>Cliente</h2>
                  <Select
                    name="device_client"
                    value={selectedClientId}
                    data={clientList}
                    disabled={loader || updatingClient}
                    onChange={(event) => handleUpdateDeviceClient(event.target.value)}
                  />
                </span>
              )}
              <span className="device-mac-field">
                <h2>Endereço Mac</h2>
                <div className="device-name-row">
                  <p className="device-name-value">{device.mac_address}</p>
                  <button
                    className="edit-name-button"
                    type="button"
                    onClick={() => {
                      setMacAddress(normalizeMacAddress(device.mac_address || ''));
                      setEditMacModal(true);
                    }}
                    title="Editar endereço MAC do dispositivo"
                  >
                    <FaPencilAlt size={14} />
                  </button>
                </div>
              </span>

              <S.Span>
                <h2>Alerta de bateria</h2>
                <Slider
                  name="battery_alert"
                  checked={batteryAlert}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setBatteryAlert(e.target.checked);

                    if (!e.target.checked || formRef.current?.getFieldValue('battery_alert_limit')) submitForm();
                  }}
                />
              </S.Span>

              <S.Span>
                <h2>Versão</h2>

                {device?.version !== undefined && (
                  <Select
                    name="version"
                    defaultValue={device.version?.toString() ?? ''}
                    disabled={loader}
                    data={versionOptions}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value !== device.version) {
                        formRef.current?.submitForm();
                      }
                    }}
                  />
                )}

              </S.Span>

              <S.Span height={batteryAlert ? 6 : 0}>
                <h2>Envio de alerta por e-mail</h2>
                <Slider
                  name="battery_email_alert"
                  checked={device.battery_email_alert || false}
                  onChange={() => {
                    if (formRef.current?.getFieldValue('battery_alert_limit')) submitForm();
                  }}
                />
              </S.Span>
              <S.Span height={batteryAlert ? 6 : 0}>
                <h2>Limite da bateria</h2>
                <Input
                  name="battery_alert_limit"
                  type="number"
                  placeholder="15"
                  defaultValue={device.battery_alert_limit}
                  showPercentage
                  disabled={loader}
                  onBlur={e => {
                    if (e.target.value !== '') submitForm();
                  }}
                />
              </S.Span>
              <span>
                <h2>Latitude</h2>
                <Input
                  name="latitude"
                  type="number"
                  defaultValue={device.latitude ?? ""}
                  disabled={loader}
                  onBlur={e => {
                    if (e.target.value.trim() !== (device.latitude ?? "")) submitForm();
                  }}
                />
              </span>
              <span>
                <h2>Longitude</h2>
                <Input
                  name="longitude"
                  type="number"
                  defaultValue={device.longitude ?? ""}
                  disabled={loader}
                  onBlur={e => {
                    if (e.target.value.trim() !== (device.longitude ?? "")) submitForm();
                  }}
                />
              </span>
              <span>
                <h2>Lat/Long pelo Dispositivo</h2>
                <p>{`${device.latitude_iot ?? ""}/ ${device.longitude_iot ?? ""}`}</p>
              </span>
              <span>
                <h2>Última atualização</h2>
                <p>{device.update_at_iot ? format(new Date(device.update_at_iot), 'dd/MM/yyyy HH:mm') : ""}</p>
              </span>

              <Grid item xs={12}>
                <span><h2>Usuário(s)</h2></span>
                <MultiSelect placeholder="" defaultValue={selectedUsers} name="multiSelectUsers"
                  onChange={async e => {
                    handleSelectChange(e);

                    if (e.length > 0) {
                      await esperar(10);
                      submitForm();
                    }
                  }}
                  options={usersForDeviceList.map(u => ({ value: u.id, label: u.name }))} style={{ width: "100%" }}>

                </MultiSelect>
              </Grid>
            </div>
          </Form>
        </div>
      </>
    );
  }, [alerts, fakeDevice, batteryAlert, device, updateDeviceFormHandler, loader, isClientAdmin, canChangeDeviceClient, selectedUsers, usersForDeviceList, selectedClientId, clientList, updatingClient, handleUpdateDeviceClient, normalizeMacAddress]);
  function formatUTC(date: Date): string {
    return `${date.getUTCDate().toString().padStart(2, '0')}/` +
      `${(date.getUTCMonth() + 1).toString().padStart(2, '0')}/` +
      `${date.getUTCFullYear()} ` +
      `${date.getUTCHours().toString().padStart(2, '0')}:` +
      `${date.getUTCMinutes().toString().padStart(2, '0')}`;
  }

  const infoSensors = useMemo(() => {
    return (
      <>
        <div className="results">
          <div className="sensorsLabel">
            <h3>Sensores</h3>
            <button type="button" className="add" onClick={() => setNewSensor(true)}>
              <AiOutlinePlus />
            </button>
          </div>
          <ul>
            {sensors.length > 0 ? (
              sensors.map(sensor => {
                const isVersion4Device = sensor.device?.version !== undefined
                  ? String(sensor.device.version) === '4'
                  : device && String(device.version) === '4';

                return (
                  <div key={sensor.id} className="card">
                    <div className="infos">
                      <span className="sensor">
                        <h2>Sensor</h2>
                        <p>{sensor.name}</p>
                      </span>
                      <span className={`port${isVersion4Device ? ' multiline' : ''}`}>
                        <h2>{isVersion4Device ? 'Leitura' : 'Porta'}</h2>
                        <p>{isVersion4Device ? sensor.reading || '-' : sensor.port}</p>
                      </span>
                      <span className="unit">
                        <h2>Unidade de medida</h2>
                        <p>{sensor.unit_of_measurement}</p>
                      </span>
                    </div>
                    <div className="edit">
                      <Link className="edit" to={`sensor/${sensor.id}`}>
                        <FaEdit />
                      </Link>
                      <button className="trash" onClick={() => setDeleteSensor(sensor.id)} type="button">
                        <HiOutlineTrash />
                      </button>
                      <Tooltip title={`Sensor ${sensor.is_enabled ? 'ativado' : 'desativado'}`}>
                        <span className={sensor.is_enabled ? 'active' : 'desative'} />
                      </Tooltip>
                    </div>
                  </div>
                );
              })
            ) : (
              <h3>Nenhum sensor encontrado</h3>
            )}
          </ul>
        </div>
      </>
    );
  }, [sensors, device]);

  const newSensorForm = useMemo(
    () => (
      <Form className="form" ref={sensorFormRef} onSubmit={sensorFormHandler}>
        <button className="close" type="button" onClick={() => setNewSensor(false)}>
          <AiOutlineClose />
        </button>
        <Grid container spacing={2} className="inputs">
          <Grid item xs={12}>
            <p>Sensor</p>
            <Input name="sensor" type="text" placeholder="pH" />
          </Grid>
          <div className="s2">
            <Grid item xs={5}>
              <p>Unidade de medida</p>
              <Input name="unit" type="text" placeholder="H3O+" />
            </Grid>

            {device && device.version && (
              <Grid item xs={5}>
                <p>Porta</p>
                {String(device.version) === '4' ? (
                  <Select name="port" data={analogPortOptions} showEmptyOption={false} />
                ) : (
                  <Input name="port" type="number" />
                )}
              </Grid>
            )}
          </div>

          {device && device.version && String(device.version) === '3' && (
            <div className="s2">
              <Grid item xs={5}>
                <p>Channel Type</p>
                <Select
                  name="channel_type"
                  data={channelTypes}
                />
              </Grid>
            </div>
          )}

          <div className="s2">
            <Grid item xs={4}>
              <p>Cumulativo</p>
              <Slider name="is_cumulative" />
            </Grid>
          </div>
        </Grid>

        <div className="butts">
          <Button type="submit">Salvar alterações</Button>
        </div>
      </Form>
    ),
    [sensorFormHandler, device],
  );

  const deviceIndicators = useMemo(() => {
    return (
      <>
        <div className="results">
          <div className="sensorsLabel">
            <h3>Indicadores</h3>
            <button className="close" type="button" onClick={() => history.push(`${device.id}/indicator`)}>
              <AiOutlinePlus />
            </button>
          </div>
          <ul>
            {indicators.length > 0 ? (
              indicators.map(indicator => (
                <div key={indicator.id} className="card">
                  <div className="infos">
                    <span className="sensor">
                      <p>{indicator.description}</p>
                    </span>
                  </div>
                  <div className="edit">
                    <Link className="edit" to={`${device.id}/indicator/${indicator.id}`}>
                      <FaEdit />
                    </Link>
                    <button className="trash" onClick={() => setDeleteIndicator(indicator.id ?? "")} type="button">
                      <HiOutlineTrash />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <h3>Nenhum indicador encontrado</h3>
            )}
          </ul>
        </div>
      </>
    );
  }, [indicators, device, history]);

  return (
    <>
      <S.GoBack>
        <Link to="/app/device" className="back">
          <RiArrowLeftSLine />
          <p>Voltar</p>
        </Link>
      </S.GoBack>
      {/* {!!error ? (
        <Advice text={error} icon={MdError} />
      ) : ( */}
      <>
        {!(deleteSensor || newSensor) && (
          <>
            <PageTitle sub="Dispositivo" title="Gerenciar" />
            <S.Container>
              <S.InfoDevice show>{infoDevice}</S.InfoDevice>
              {/* <S.InfoDevice show> {staticInfoDevice} </S.InfoDevice> */}
              <div className="separator" />
              <S.InfoSensors show>{infoSensors}</S.InfoSensors>
              {/* <div className="separator"></div> */}
              {/* <S.InfoSensors show>{deviceIndicators}</S.InfoSensors> */}
              {/* <S.InfoSensors show> {staticInfoSensors} </S.InfoSensors> */}
            </S.Container>
          </>
        )}
        {!!deleteSensor && (
          <S.DeletePopup>
            <div>
              <FiAlertTriangle size={30} />
              <p>Deseja excluir o sensor?</p>
              <div className="buttons">
                <Button className="cancel" onClick={() => setDeleteSensor('')}>
                  Cancelar
                </Button>
                <Button className="delete" onClick={() => handleDeleteSensor(deleteSensor)}>
                  Excluir
                </Button>
              </div>
            </div>
          </S.DeletePopup>
        )}
        {!!deleteIndicator && (
          <S.DeletePopup>
            <div>
              <FiAlertTriangle size={30} />
              <p>Deseja excluir o indicador?</p>
              <div className="buttons">
                <Button className="cancel" onClick={() => setDeleteIndicator('')}>
                  Cancelar
                </Button>
                <Button className="delete" onClick={() => handleDeleteIndicator(deleteIndicator)}>
                  Excluir
                </Button>
              </div>
            </div>
          </S.DeletePopup>
        )}
        {newSensor && <S.NewSensorPopup>{newSensorForm}</S.NewSensorPopup>}
        {editNameModal && (
          <S.EditNamePopup>
            <div className="modal-content">
              <h3>Editar Nome do Dispositivo</h3>
              <p className="description">Atualize o nome que será exibido para este dispositivo.</p>
              <textarea
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="Digite o nome do dispositivo"
                autoFocus
                maxLength={120}
                rows={3}
                className="device-name-input"
              />
              <span className="char-counter">{deviceName.length}/120</span>
              <div className="buttons">
                <Button className="cancel" onClick={() => {
                  setEditNameModal(false);
                  setDeviceName('');
                }}>
                  Cancelar
                </Button>
                <Button className="save" onClick={handleUpdateDeviceName}>
                  Salvar
                </Button>
              </div>
            </div>
          </S.EditNamePopup>
        )}
        {editMacModal && (
          <S.EditNamePopup>
            <div className="modal-content">
              <h3>Editar Endereço MAC</h3>
              <p className="description">Use o formato AA:BB:CC:DD:EE:FF.</p>
              <input
                value={macAddress}
                onChange={(e) => setMacAddress(normalizeMacAddress(e.target.value))}
                placeholder="AA:BB:CC:DD:EE:FF"
                autoFocus
                maxLength={17}
                className="mac-address-input"
              />
              <span className="char-counter">{macAddress.length}/17</span>
              <div className="buttons">
                <Button className="cancel" onClick={() => {
                  setEditMacModal(false);
                  setMacAddress('');
                }}>
                  Cancelar
                </Button>
                <Button className="save" onClick={handleUpdateMacAddress}>
                  Salvar
                </Button>
              </div>
            </div>
          </S.EditNamePopup>
        )}
        {showAlertsModal && (
          <S.AlertsModal>
            <div>
              <button type="button" className="close" onClick={() => setShowAlertsModal(false)}>
                <AiOutlineClose />
              </button>
              <h3>Histórico de Alertas</h3>
              <div className="alerts-content">
                {alerts.length > 0 ? (
                  <ul>
                    {alerts.map((alert, index) => (
                      <li key={`${alert.id}-${index}`}>
                        <p className="date">{formatUTC(new Date(alert.timestamp))}</p>
                        <p>{alert.message}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Nenhum alerta encontrado</p>
                )}
              </div>
            </div>
          </S.AlertsModal>
        )}
      </>
      {/* )} */}
    </>
  );
};

export default EditDevices;
